import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../ui/index.ts";
import type { Patient } from "../../core/services/patients.service.ts";
import { I } from "../icons/icons.tsx";
import {
  comparePatientsBySearch,
  formatPatientDisplayName,
  patientMatchesSearch,
} from "./patient-name.ts";
import { PatientAvatar } from "./patient-avatar.component.tsx";

const inputClass =
  "h-9 w-full rounded-[var(--sf-radius)] border border-[var(--sf-border-strong)] bg-[var(--sf-surface)] px-2.5 pr-8 text-[13.5px] font-[inherit] text-[var(--sf-text)] outline-none focus:border-[var(--sf-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]";

export function PatientCombobox({
  patients,
  value,
  onChange,
  placeholder = "Rechercher un patient…",
  className,
  autoFocus,
}: {
  patients: Patient[];
  value: string;
  onChange: (name: string, patient?: Patient) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const [rect, setRect] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const listId = React.useId();

  const filtered = React.useMemo(() => {
    const q = value.trim();
    return patients
      .filter((p) => !q || patientMatchesSearch(p.name, q))
      .sort((a, b) => comparePatientsBySearch(a.name, b.name, q))
      .slice(0, 8);
  }, [patients, value]);

  React.useEffect(() => {
    setHighlight(0);
  }, [value, open]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const pick = (p: Patient) => {
    onChange(p.name, p);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open && filtered[highlight]) {
      e.preventDefault();
      pick(filtered[highlight]!);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <div ref={triggerRef} className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={inputClass}
          autoComplete="off"
          autoFocus={autoFocus}
        />
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sf-text-muted">
          <I.ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </span>
      </div>

      {open &&
        rect &&
        filtered.length > 0 &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 600,
            }}
            className="max-h-[min(280px,40vh)] overflow-y-auto rounded-[var(--sf-radius-lg)] border border-sf-border bg-white py-1 shadow-[var(--sf-shadow-lg)]"
          >
            {filtered.map((p, i) => (
              <li key={p.uuid} role="option" aria-selected={i === highlight}>
                <button
                  type="button"
                  tabIndex={-1}
                  data-option-index={i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(p)}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 border-none px-2.5 py-2 text-left font-[inherit] transition-colors",
                    i === highlight
                      ? "bg-sf-primary-50"
                      : "bg-transparent hover:bg-gray-50",
                  )}
                >
                  <PatientAvatar patient={p} size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-sf-text">
                      {formatPatientDisplayName(p.name)}
                    </div>
                    <div className="truncate text-[11px] text-sf-text-muted">
                      {p.age} ans · {p.tag}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}

      {open &&
        rect &&
        value.trim() &&
        filtered.length === 0 &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 600,
            }}
            className="rounded-[var(--sf-radius-lg)] border border-sf-border bg-white px-3 py-2.5 text-[12.5px] text-sf-text-muted shadow-[var(--sf-shadow-lg)]"
          >
            Aucun patient trouvé
          </div>,
          document.body,
        )}
    </div>
  );
}
